// Workspace menu override
//
// A workspace can set `menuOverride` (Json) to trim what shows in the sidebar
// and adjust a few related surfaces. Shape:
//   {
//     allowedItems: string[],              // hrefs to keep in nav
//     landingHref?: string,                 // where /dashboard redirects to
//     allowedSharedChatContextTypes?: string[]  // filter Chats list
//   }
//
// If `menuOverride` is null/missing, behavior is unchanged.

import { isHrefVisible } from "@/lib/plugins";

function stripHash(href) {
  if (typeof href !== "string") return href;
  const i = href.indexOf("#");
  return i === -1 ? href : href.slice(0, i);
}

export function getMenuOverride(workspace) {
  const override = workspace?.menuOverride;
  if (!override || typeof override !== "object") return null;
  return override;
}

export function getLandingHref(workspace) {
  const override = getMenuOverride(workspace);
  const href = override?.landingHref;
  if (typeof href !== "string" || href.length === 0) return null;
  // A landing page belonging to a switched-off plugin would bounce back to "/",
  // which redirects here again — so treat it as unconfigured (SAL-2912).
  if (!isHrefVisible(workspace, href)) return null;
  return href;
}

export function getAllowedSharedChatContextTypes(workspace) {
  const override = getMenuOverride(workspace);
  const list = override?.allowedSharedChatContextTypes;
  return Array.isArray(list) ? list : null;
}

// Filter a nav item tree by an `allowedItems` href list.
// - Items with an href are kept iff href (sans #fragment) is in the allow-list.
// - Parent items (no href, or href "#") are kept iff at least one child survives.
// - Items already hidden via display:false are dropped.
// Returns the original items unchanged if the workspace has no menuOverride.
export function applyMenuOverride(items, workspace) {
  const override = getMenuOverride(workspace);
  if (!override || !Array.isArray(override.allowedItems)) return items;
  const allowed = new Set(override.allowedItems);
  return filterItems(items, allowed);
}

function filterItems(items, allowed) {
  const result = [];
  for (const item of items) {
    if (!item) continue;
    if (item.display === false) continue;
    const filteredSubItems = Array.isArray(item.subItems)
      ? filterItems(item.subItems, allowed)
      : null;
    const hasHref = typeof item.href === "string" && item.href !== "#";
    const hrefAllowed = hasHref && allowed.has(stripHash(item.href));
    const keep =
      hrefAllowed || (filteredSubItems && filteredSubItems.length > 0);
    if (!keep) continue;
    result.push({
      ...item,
      ...(Array.isArray(item.subItems) ? { subItems: filteredSubItems } : {}),
    });
  }
  return result;
}
